import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { User, Department, Procedure, Activity, UserRole, JobRoleTitle } from "../types";
import { PersonDepartments } from "../components/PersonDepartments";
import { toast } from "sonner";
import {
  ArrowLeft, Edit, Trash2, Mail, Phone, Briefcase,
  Building2, Shield, FileText, Users, Archive, RefreshCw, AlertCircle, User as UserIcon, History, Copy,
  Cake, Calendar, Clock, MoreVertical, ChevronDown, Loader2, Package, Eye, Sparkles, Monitor, DollarSign,
  UserCog, Warehouse, GraduationCap, Globe2, MapPin, HeartHandshake, Accessibility
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { MultiSelect } from "../components/ui/multi-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import FileAttachments from "../components/FileAttachments";
import { getDepartmentCode, calculateTimeWithCompany, toSydneyDate } from "../utils/helpers";
import api from "../utils/api";
import { Can } from "../components/Can";
import { CanAny } from "../components/CanAny";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

import { ActivityLog } from "../components/ActivityLog";
import CorrespondenceTab from "../components/CorrespondenceTab";
import { MemosAndPoliciesTab } from "../components/MemosAndPoliciesTab";
import AssetsTab from "../components/AssetsTab";
import AccessRestricted from "../components/AccessRestricted";
import { SkillsAndInterestsTab } from "../components/SkillsAndInterestsTab";
import { AttendanceTab } from "../components/AttendanceTab";
import { CalendarCheck } from "lucide-react";
import { QualificationsTab } from "../components/qualifications/QualificationsTab";
import { PersonLearningTab } from "../components/learning/PersonLearningTab";
import { RemunerationTab } from "../components/RemunerationTab";
import { canViewProfile, isGlobalRole, isOwnerRole } from "../utils/user-logic";
import { useHierarchy } from '../hooks/useHierarchy';

const getUser = async (id: any) => {
  return api.get(`/api/users/${id}`, { skipErrorToast: true });
}

const PersonPage = () => {
  const { id } = useParams<{ id: string }>();
  const { users, fetchManagerUsers, managerUsers, departments, procedures, activities, updateUser, deleteUser, unarchiveUser, jobRoles, fetchProcedures, updateActivity, externalPartners } = useData();
  const { currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [userDepartment, setUserDepartment] = useState<Department | null>(null);
  const [userProcedures, setUserProcedures] = useState<Procedure[]>([]);
  const [userActivities, setUserActivities] = useState<Activity[]>([]);
  const [userManager, setUserManager] = useState<User | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(false);
  const [profileViewers, setProfileViewers] = useState<any[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersExpanded, setViewersExpanded] = useState(false);
  const [userBusinessSystems, setUserBusinessSystems] = useState<any[]>([]);
  const [businessSystemsLoading, setBusinessSystemsLoading] = useState(false);
  const [archiveBlockers, setArchiveBlockers] = useState<any[]>([]);
  const [archiveChecksLoading, setArchiveChecksLoading] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);
  const [newOwnerId, setNewOwnerId] = useState<string>("");
  const [transferLoading, setTransferLoading] = useState(false);
  // Activity RACI transfer dialog state
  const [activityTransferDialogOpen, setActivityTransferDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedActivityRole, setSelectedActivityRole] = useState<string>("");
  const [raciNewValue, setRaciNewValue] = useState<string>("");
  const [raciMultiValue, setRaciMultiValue] = useState<string[]>([]);
  const [activityTransferLoading, setActivityTransferLoading] = useState(false);
  // Batch transfer state
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<string[]>([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState<string[]>([]);
  const [batchTransferDialogOpen, setBatchTransferDialogOpen] = useState(false);
  const [batchTransferType, setBatchTransferType] = useState<'procedures' | 'activities'>('procedures');
  const [batchNewOwnerId, setBatchNewOwnerId] = useState<string>("");
  const [batchTransferLoading, setBatchTransferLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ability = useAbility(AbilityContext);
  const location = useLocation();
  const { canManageUser } = useHierarchy();

  useEffect(() => {
    if (!id || !currentUser) return;
    
    if (canViewProfile(id, currentUser, ability, canManageUser) || isGlobalRole(currentUser.userRole) || isOwnerRole(currentUser.userRole)) {
      setError(null);
    } else {
      setError("Access Restricted");
    }
  }, [id, currentUser, ability, canManageUser]);

  useEffect(() => {
    if (managerUsers.length === 0) fetchManagerUsers();
  }, []);

  // Fetch profile viewers
  useEffect(() => {
    const fetchProfileViewers = async () => {
      if (!id) return;
      try {
        setViewersLoading(true);
        const data = await api.get(`/api/users/${id}/profile-viewers`);
        if (data && data.success) {
          setProfileViewers(data.viewers);
        }
      } catch (error) {
        console.error('Error fetching profile viewers:', error);
      } finally {
        setViewersLoading(false);
      }
    };
    fetchProfileViewers();
  }, [id]);

  useEffect(() => {
    const fetchBusinessSystems = async () => {
      if (!id) return;
      try {
        setBusinessSystemsLoading(true);
        const data = await api.get(`/api/business-systems/user/${id}`);
        setUserBusinessSystems(data);
      } catch (error) {
        console.error('Error fetching business systems:', error);
      } finally {
        setBusinessSystemsLoading(false);
      }
    };
    fetchBusinessSystems();
  }, [id]);

  useEffect(() => {
    const fetchArchiveChecks = async () => {
      if (!id) return;
      try {
        setArchiveChecksLoading(true);
        const data = await api.get(`/api/users/${id}/archive-checks`);
        if (data && data.success) {
          setArchiveBlockers(data.blockers || []);
        }
      } catch (error) {
        console.error('Error fetching archive checks:', error);
      } finally {
        setArchiveChecksLoading(false);
      }
    };
    fetchArchiveChecks();
  }, [id]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      // Legacy links to the removed Performance Development tab now land on Correspondence
      setActiveTab(tab === "performance_development" ? "correspondence" : tab);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const data = await getUser(id);

        if (data && data.success) {
          setUser(data.user);
          setUserActivities(data.activities || []);
          setAttachments(data.user?.attachments || []);
          setError(prev => prev === "Access Restricted" ? prev : null);
        } else if (data && (data.message === "Forbidden" || data.message === "Access Restricted")) {
          setError("Access Restricted");
        } else {
          setError(prev => prev === "Access Restricted" ? prev : "Error fetching user details");
        }
      } catch (err: any) {
        if (err.status === 404) {
          setError("User not found");
        } else if (err.status === 403 || (err.response && err.response.status === 403)) {
          setError("Access Restricted");
        } else {
          console.error("Error fetching user:", err);
          setError(prev => prev === "Access Restricted" ? prev : "Error fetching user details");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  // Separate effect to handle manager/department mapping when background data arrives
  useEffect(() => {
    if (!user) return;

    if (user.manager) {
      const manager = managerUsers.find(u => u.id == user.manager);
      if (manager) setUserManager(manager);
    }

    if (departments.length > 0) {
      const foundDepartment = departments.find(
        (dept) => dept.departmentCode == user.department
      );
      if (foundDepartment) setUserDepartment(foundDepartment);
    }
  }, [user, departments, managerUsers]);

  useEffect(() => {
    if (departments && user) {
      const foundDepartment = departments.find(
        (dept) => dept.departmentCode == user.department
      );
      if (foundDepartment) {
        setUserDepartment(foundDepartment);
      }
    }
  }, [departments, user]);

  useEffect(() => {
    if (procedures && user) {
      const foundProcedures = procedures.filter(
        (proc) => typeof proc.procedureOwner === 'object' ? proc.procedureOwner?.id === user.id : proc.procedureOwner == user.id
      );
      setUserProcedures(foundProcedures);
    }
  }, [procedures, user]);

  if (error === "Access Restricted") {
    return (
      <AccessRestricted backMessage="Back to My Profile" accessMessage="You don't have permission to access this profile." navigateTo={`/people/${currentUser?.id}`} />
    );
  }

  if (error && error !== "User not found") {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
        <AlertCircle size={48} className="text-destructive" />
        <h2 className="text-xl font-semibold">Error</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate("/people")}>
          Back to People
        </Button>
      </div>
    );
  }

  if (error === "User not found") {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
        <UserIcon size={48} className="text-muted-foreground" />
        <h2 className="text-xl font-semibold">User Not Found</h2>
        <p className="text-muted-foreground">The user you are looking for does not exist.</p>
        <Button variant="outline" onClick={() => navigate("/people")}>
          Back to People
        </Button>
      </div>
    );
  }

  if (isLoading || !user) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
        <p className="text-slate-500 font-medium animate-pulse text-sm uppercase tracking-widest">Loading person details...</p>
      </div>
    );
  }

  const handleDelete = () => {
    deleteUser(id!);
    setOpenDelete(false);
    navigate("/people");
  };

  const handleUnarchive = () => {
    if (!currentUser || !id) return;
    console.log('Current user for unarchiving:', currentUser);
    unarchiveUser(id, currentUser.id);
    setOpenUnarchive(false);
    // Update local state
    setUser({
      ...user!,
      archived: false,
      archivedOn: undefined,
      archivedBy: undefined
    });
  };

  const handleTransferOwnership = async () => {
    if (!selectedProcedure || !newOwnerId) return;
    try {
      setTransferLoading(true);
      const data = await api.patch(`/api/procedures/${selectedProcedure.id}/transfer-ownership`, {
        newOwnerId
      });
      if (data.success) {
        toast.success("Procedure ownership transferred successfully");
        // Refresh global procedures so the UI updates without a page reload
        await fetchProcedures();
        setTransferDialogOpen(false);
        setSelectedProcedure(null);
        setNewOwnerId("");
      } else {
        toast.error(data.message || "Failed to transfer ownership");
      }
    } catch (error: any) {
      console.error("Transfer ownership error:", error);
      toast.error(error.message || "Failed to transfer ownership");
    } finally {
      setTransferLoading(false);
    }
  };

  const openTransferDialog = (procedure: Procedure) => {
    setSelectedProcedure(procedure);
    setNewOwnerId("");
    setTransferDialogOpen(true);
  };

  // Helper to determine the user's RACI role in an activity
  const getUserRaciRole = (activity: Activity): string => {
    if (!activity.raci) return "Unknown";
    if (activity.raci.responsiblePerson?.id == id) return "Responsible";
    if (activity.raci.accountablePerson?.id == id) return "Accountable";
    if (activity.raci.consultedPerson?.some((p: any) => p.id == id)) return "Consulted";
    if (activity.raci.informedPerson?.some((p: any) => p.id == id)) return "Informed";
    return "Unknown";
  };

  // Open activity RACI transfer dialog
  const openActivityTransferDialog = (activity: Activity) => {
    const role = getUserRaciRole(activity);
    setSelectedActivity(activity);
    setSelectedActivityRole(role);
    // Pre-populate with current assignment using type-id format
    if (role === "Responsible" || role === "Accountable") {
      const raciKey = role === "Responsible" ? "responsiblePerson" : "accountablePerson";
      const currentAssignee = activity.raci?.[raciKey];
      setRaciNewValue(currentAssignee?.id ? `${currentAssignee.type}-${currentAssignee.id}` : "");
      setRaciMultiValue([]);
    } else if (role === "Consulted" || role === "Informed") {
      const raciKey = role === "Consulted" ? "consultedPerson" : "informedPerson";
      const currentAssignees = activity.raci?.[raciKey] || [];
      setRaciMultiValue(currentAssignees.map((p: any) => `${p.type}-${p.id}`));
      setRaciNewValue("");
    } else {
      setRaciNewValue("");
      setRaciMultiValue([]);
    }
    setActivityTransferDialogOpen(true);
  };

  // Batch selection handlers for procedures
  const handleProcedureToggle = (procedureId: string) => {
    setSelectedProcedureIds(prev =>
      prev.includes(procedureId)
        ? prev.filter(id => id !== procedureId)
        : [...prev, procedureId]
    );
  };

  const handleProcedureSelectAll = () => {
    const allSelected = userProcedures.length > 0 && userProcedures.every(p => selectedProcedureIds.includes(p.id));
    if (allSelected) {
      setSelectedProcedureIds([]);
    } else {
      setSelectedProcedureIds(userProcedures.map(p => p.id));
    }
  };

  // Batch selection handlers for activities
  const handleActivityToggle = (activityId: string) => {
    setSelectedActivityIds(prev =>
      prev.includes(activityId)
        ? prev.filter(id => id !== activityId)
        : [...prev, activityId]
    );
  };

  const handleActivitySelectAll = () => {
    const allSelected = userActivities.length > 0 && userActivities.every(a => selectedActivityIds.includes(a.id));
    if (allSelected) {
      setSelectedActivityIds([]);
    } else {
      setSelectedActivityIds(userActivities.map(a => a.id));
    }
  };

  // Open batch transfer dialog
  const openBatchTransferDialog = (type: 'procedures' | 'activities') => {
    setBatchTransferType(type);
    setBatchNewOwnerId("");
    setBatchTransferDialogOpen(true);
  };

  // Check if selected activities can be batch transferred (all same role)
  const canBatchTransferActivities = () => {
    if (selectedActivityIds.length <= 1) return true;
    const selectedActs = userActivities.filter(a => selectedActivityIds.includes(a.id));
    const roles = selectedActs.map(a => getUserRaciRole(a));
    const uniqueRoles = [...new Set(roles)];
    return uniqueRoles.length === 1;
  };

  // Handle batch transfer
  const handleBatchTransfer = async () => {
    if (!batchNewOwnerId) return;
    try {
      setBatchTransferLoading(true);
      if (batchTransferType === 'procedures') {
        // Transfer all selected procedures
        await Promise.all(
          selectedProcedureIds.map(procId =>
            api.patch(`/api/procedures/${procId}/transfer-ownership`, { newOwnerId: batchNewOwnerId })
          )
        );
        toast.success(`Transferred ${selectedProcedureIds.length} procedure(s) successfully`);
        await fetchProcedures();
        setSelectedProcedureIds([]);
      } else {
        // For activities, we need to check all selected activities have the same role
        const selectedActs = userActivities.filter(a => selectedActivityIds.includes(a.id));
        const roles = selectedActs.map(a => getUserRaciRole(a));
        const uniqueRoles = [...new Set(roles)];
        if (uniqueRoles.length > 1) {
          toast.error("Selected activities have different roles. Please select activities with the same role.");
          return;
        }
        const role = uniqueRoles[0];
        // Build RACI update for all selected activities
        const raciUpdate: any = {};
        if (role === "Responsible" || role === "Accountable") {
          const raciKey = role === "Responsible" ? "responsiblePerson" : "accountablePerson";
          if (batchNewOwnerId === 'user-none') {
            raciUpdate[raciKey] = null;
          } else {
            const [type, id] = batchNewOwnerId.split('-');
            raciUpdate[raciKey] = { id, type };
          }
        } else if (role === "Consulted" || role === "Informed") {
          const raciKey = role === "Consulted" ? "consultedPerson" : "informedPerson";
          const newArray = batchNewOwnerId.split(',').map((v: string) => {
            const [type, ...idParts] = v.split('-');
            return { id: idParts.join('-'), type };
          });
          raciUpdate[raciKey] = newArray;
        }
        await Promise.all(
          selectedActs.map(act => {
            const procedure = typeof act.procedure !== 'string' ? act.procedure : null;
            const procedureId = procedure?.id || act.procedure as string;
            return updateActivity(procedureId, act.id, {
              raci: { ...act.raci, ...raciUpdate }
            });
          })
        );
        toast.success(`Updated ${selectedActivityIds.length} activity(s) successfully`);
        // Update local state
        setUserActivities(prev => prev.filter(a => !selectedActivityIds.includes(a.id)));
        setSelectedActivityIds([]);
      }
      setBatchTransferDialogOpen(false);
      setBatchNewOwnerId("");
    } catch (error: any) {
      console.error("Batch transfer error:", error);
      toast.error(error.message || "Failed to transfer");
    } finally {
      setBatchTransferLoading(false);
    }
  };

  // Handle RACI transfer for an activity
  const handleActivityRaciTransfer = async () => {
    if (!selectedActivity || !selectedActivityRole) return;
    try {
      setActivityTransferLoading(true);
      const procedure = typeof selectedActivity.procedure !== 'string' ? selectedActivity.procedure : null;
      const procedureId = procedure?.id || selectedActivity.procedure as string;
      if (!procedureId) {
        toast.error("Could not determine procedure for this activity");
        return;
      }

      let raciUpdate: any = {};

      if (selectedActivityRole === "Responsible" || selectedActivityRole === "Accountable") {
        // Single select: transfer to new party (User, JobRole, or ExternalPartner)
        if (!raciNewValue) {
          toast.error("Please select a new assignee");
          return;
        }
        // Handle "None" option
        if (raciNewValue === 'user-none') {
          const raciKey = selectedActivityRole === "Responsible" ? "responsiblePerson" : "accountablePerson";
          raciUpdate[raciKey] = null;
        } else {
          const [type, id] = raciNewValue.split('-');
          const raciKey = selectedActivityRole === "Responsible" ? "responsiblePerson" : "accountablePerson";
          raciUpdate[raciKey] = { id, type };
        }
      } else if (selectedActivityRole === "Consulted" || selectedActivityRole === "Informed") {
        // Multi select: update the array with parsed type-id values
        const raciKey = selectedActivityRole === "Consulted" ? "consultedPerson" : "informedPerson";
        const newArray = raciMultiValue.map((v: string) => {
          const [type, ...idParts] = v.split('-');
          return { id: idParts.join('-'), type };
        });
        raciUpdate[raciKey] = newArray;
      }

      const data = await updateActivity(procedureId, selectedActivity.id, {
        raci: { ...selectedActivity.raci, ...raciUpdate }
      });

      if (data.success) {
        toast.success("Activity role updated successfully");
        // Update local userActivities state
        setUserActivities(prev => prev.map(a => {
          if (a.id !== selectedActivity.id) return a;
          const updatedActivity = { ...a, raci: { ...a.raci, ...raciUpdate } };
          // Check if user is still in any RACI role after update
          const stillResponsible = updatedActivity.raci?.responsiblePerson?.id == id;
          const stillAccountable = updatedActivity.raci?.accountablePerson?.id == id;
          const stillConsulted = updatedActivity.raci?.consultedPerson?.some((p: any) => p.id == id);
          const stillInformed = updatedActivity.raci?.informedPerson?.some((p: any) => p.id == id);
          if (!stillResponsible && !stillAccountable && !stillConsulted && !stillInformed) {
            return null; // Remove from list
          }
          return updatedActivity;
        }).filter(Boolean) as Activity[]);
        setActivityTransferDialogOpen(false);
        setSelectedActivity(null);
        setSelectedActivityRole("");
        setRaciNewValue("");
        setRaciMultiValue([]);
      } else {
        toast.error(data.message || "Failed to update activity role");
      }
    } catch (error: any) {
      console.error("Activity RACI transfer error:", error);
      toast.error(error.message || "Failed to update activity role");
    } finally {
      setActivityTransferLoading(false);
    }
  };

  // Helper function to get user name
  const getUserName = (userId: string) => {
    if (!userId) return "Unknown";
    if (user && user.id == userId) return user.name;
    if (currentUser && currentUser.id == userId) return currentUser.name;
    const foundUser = users.find(u => u.id == userId) || managerUsers.find(u => u.id == userId);
    return foundUser ? foundUser.name : `User #${userId}`;
  };

  // Helper function to format archive date
  const formatArchiveDate = (dateString: string) => {
    if (!dateString) return "Unknown";
    return toSydneyDate(dateString);
  };

  // Helper functions to count user associations
  const getManagedPeopleCount = () => {
    return users.filter(u => u.manager == id && !u.archived).length;
  };

  const getOwnedProceduresCount = () => {
    return procedures.filter(p => p.procedureOwner == id && !p.archived).length;
  };

  const getResponsibleActivitiesCount = () => {
    return activities.filter(a => a.responsiblePerson == id).length;
  };

  const getAccountableActivitiesCount = () => {
    return activities.filter(a => a.accountablePerson == id).length;
  };

  // Service Desk Team Admin / Yard Manager positions block deletion until the user is demoted
  const getManagerPositionBlockers = () => {
    return archiveBlockers.filter(b => b.type === 'service_desk_admin' || b.type === 'yard_manager');
  };

  const hasHistoricallyOwnedProcedures = () => {
    // Check if user has ever owned any procedures (including archived ones and deleted ones)
    return procedures.some(p =>
      // Check current ownership
      (typeof p.procedureOwner === 'string' ? p.procedureOwner == id : p.procedureOwner?.id == id) ||
      // Check if they created the procedure
      (typeof p.createdBy === 'string' ? p.createdBy == id : p.createdBy?.id == id) ||
      // Check if they modified the procedure
      (typeof p.lastModifiedBy === 'string' ? p.lastModifiedBy == id : p.lastModifiedBy?.id == id) ||
      // Check activity log for past ownership
      p.activityLogs?.some(log => {
        const userName = users.find(u => u.id == id)?.name;
        return log.changeDescription?.includes(`Procedure owner changed from ${userName}`);
      })
    );
  };

  const hasHistoricalActivities = () => {
    // Check if user has ever been responsible, accountable, consulted, or informed in any activity
    return activities.some(a =>
      a.responsiblePerson == id ||
      a.accountablePerson == id ||
      a.consultedPerson == id ||
      a.informedPerson == id
    );
  };

  const canDeleteUser = () => {
    if (getManagedPeopleCount() > 0) return false;
    if (getOwnedProceduresCount() > 0) return false;
    if (hasHistoricallyOwnedProcedures() || hasHistoricalActivities()) return false;
    if (getManagerPositionBlockers().length > 0) return false;

    return true;
  };

  // Handle attachments change
  const handleAttachmentsChange = (newAttachments: any[]) => {
    setAttachments(newAttachments);
    if (user) {

      const fetchUser = async () => {
        const data = await getUser(id);

        if (data && data.success) {
          setUser(data.user);
        }
      };

      fetchUser();
    }
  };

  // Build RACI single-select options for SearchableSelect (Responsible/Accountable)
  // Same format as activitypage.tsx: Users, Job Roles, External Partners
  const raciSingleSelectGroups = [
    {
      heading: "Users",
      options: Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
        .filter(u => ['Worker', 'Team Lead', 'Supervisor', 'Manager', 'Senior Manager', 'Director', 'Admin', 'Account Owner'].includes(u.userRole))
        .filter(u => !u.archived)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(user => ({
          label: user.name,
          value: `User-${user.id}`,
          subtitle: user.jobRole,
        })),
    },
    {
      heading: "Job Roles",
      options: jobRoles.map((role) => ({
        label: `${role.title} (Role)`,
        value: `JobRole-${role.id}`,
      })),
    },
    {
      heading: "External Partners",
      options: externalPartners.map((partner) => ({
        label: `${partner.name} (External)`,
        value: `ExternalPartner-${partner.id}`,
      })),
    },
  ];

  // Build RACI multi-select options for MultiSelect (Consulted/Informed)
  // Same format as activitypage.tsx
  const raciMultiSelectOptions = [
    ...Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
      .filter(u => ['Worker', 'Team Lead', 'Supervisor', 'Manager', 'Senior Manager', 'Director', 'Admin', 'Account Owner'].includes(u.userRole))
      .filter(u => !u.archived)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
    ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
    ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` })),
  ];

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="flex-shrink-0"
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold truncate">{user?.name || "Person Profile"}</h1>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-orange-100 border-orange-200 text-orange-800 hover:bg-orange-100">
                People
              </Badge>
              {user.archived && (
                <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800">
                  <Archive size={12} />
                  Archived
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3">
          {!user.archived && (
            <Can I={ACTIONS.UPDATE} a={RESOURCES.PERSON}>
              <Button variant="outline" onClick={() => navigate(`/people/edit/${id}`, { state: { from: location.pathname } })} className="flex items-center gap-2 w-full sm:w-auto">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </Can>
          )}

          {!user.archived && (
            <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PERSON}>
              <div className="flex flex-col items-end gap-1">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/people/${id}/archive`)}
                  className={
                    user.archiveDraft
                      ? 'flex items-center gap-2 w-full sm:w-auto border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800'
                      : 'flex items-center gap-2 w-full sm:w-auto'
                  }
                >
                  {user.archiveDraft ? <Clock size={16} /> : <Archive size={16} />}
                  {user.archiveDraft ? 'Archiving In Progress' : 'Archive'}
                </Button>
                {user.archiveDraft && (
                  <span className="text-xs text-amber-700">
                    Started by {user.archiveDraft.startedBy?.name || 'Unknown'} · {toSydneyDate(user.archiveDraft.startedAt)}
                  </span>
                )}
              </div>
            </Can>
          )}

          {user.archived && (
            <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PERSON}>
              <Button variant="outline" onClick={() => setOpenUnarchive(true)} className="flex items-center gap-2 w-full sm:w-auto">
                <RefreshCw size={16} />
                Unarchive
              </Button>
            </Can>
          )}

          {!user.archived && (
            <Can I={ACTIONS.DELETE} a={RESOURCES.PERSON}>
              <Button
                variant="destructive"
                className="border-red-200 hover:bg-red-100 w-full"
                onClick={() => setOpenDelete(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </Can>
          )}
        </div>

        {/* Mobile Actions Dropdown */}
        <div className="md:hidden flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2 w-full rounded-xl border-gray-200 font-bold shadow-sm h-10 px-4">
                <MoreVertical size={16} />
                Actions
                <ChevronDown size={14} className="text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl shadow-lg border-gray-100">
              {!user.archived && (
                <Can I={ACTIONS.UPDATE} a={RESOURCES.PERSON}>
                  <DropdownMenuItem onClick={() => navigate(`/people/edit/${id}`, { state: { from: location.pathname } })} className="flex items-center gap-2 cursor-pointer py-2.5">
                    <Edit className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Edit</span>
                  </DropdownMenuItem>
                </Can>
              )}

              {!user.archived && (
                <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PERSON}>
                  <DropdownMenuItem onClick={() => navigate(`/people/${id}/archive`)} className="flex items-center gap-2 cursor-pointer py-2.5">
                    {user.archiveDraft ? (
                      <Clock className="h-4 w-4 text-amber-600" />
                    ) : (
                      <Archive className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="font-medium">{user.archiveDraft ? 'Archiving In Progress' : 'Archive'}</span>
                  </DropdownMenuItem>
                </Can>
              )}

              {user.archived && (
                <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PERSON}>
                  <DropdownMenuItem onClick={() => setOpenUnarchive(true)} className="flex items-center gap-2 cursor-pointer py-2.5">
                    <RefreshCw className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">Unarchive</span>
                  </DropdownMenuItem>
                </Can>
              )}

              {!user.archived && (
                <Can I={ACTIONS.DELETE} a={RESOURCES.PERSON}>
                  <DropdownMenuItem
                    onClick={() => setOpenDelete(true)}
                    className="flex items-center gap-2 cursor-pointer py-2.5 text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="font-medium">Delete</span>
                  </DropdownMenuItem>
                </Can>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Archive warning banner */}
      {user.archived && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-yellow-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-800">This person has been archived</h3>
                <p className="text-sm text-yellow-700">
                  Archived on {formatArchiveDate(user.archivedOn || "")} by {getUserName(user.archivedBy || "")}
                  {user.terminationDate && ` · Termination date ${toSydneyDate(user.terminationDate)}`}
                  {user.terminationReason && ` (${user.terminationReason})`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - full width below header */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Mobile Dropdown for Tabs */}
        <div className="md:hidden mb-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between bg-white rounded-xl border-gray-200 shadow-sm h-11">
                <div className="flex items-center gap-2">
                  {activeTab === "overview" && <Users size={18} className="text-blue-500" />}
                  {activeTab === "procedures" && <FileText size={18} className="text-blue-500" />}
                  {activeTab === "remuneration" && <DollarSign size={18} className="text-blue-500" />}
                  {activeTab === "skills_interests" && <Sparkles size={18} className="text-blue-500" />}
                  {activeTab === "correspondence" && <Mail size={18} className="text-blue-500" />}
                  {activeTab === "memos_policies" && <FileText size={18} className="text-blue-500" />}
                  {activeTab === "assets" && <Package size={18} className="text-blue-500" />}
                  {activeTab === "attendance" && <CalendarCheck size={18} className="text-blue-500" />}
                  {activeTab === "business_systems" && <Monitor size={18} className="text-blue-500" />}
                  {activeTab === "learning" && <GraduationCap size={18} className="text-blue-500" />}
                  {activeTab === "attachments" && <FileText size={18} className="text-blue-500" />}
                  {activeTab === "activity_log" && <History size={18} className="text-blue-500" />}
                  <span className="font-semibold text-gray-900 uppercase text-[10px] tracking-wider">
                    {activeTab === "overview" ? "Overview" :
                      activeTab === "procedures" ? "Procedures" :
                        activeTab === "skills_interests" ? "Skills & Interests" :
                          activeTab === "correspondence" ? "Correspondence" :
                                activeTab === "memos_policies" ? "Memos & Policies" :
                                  activeTab === "assets" ? "Assets" :
                                    activeTab === "attendance" ? "Attendance & Leave" :
                                      activeTab === "business_systems" ? "Business Systems" :
                                        activeTab === "learning" ? "Learning" :
                                        activeTab === "attachments" ? "Attachments" : "Activity Logs"}
                  </span>
                </div>
                <ChevronDown size={18} className="text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[calc(100vw-3rem)] rounded-xl shadow-lg border-gray-100">
              <DropdownMenuItem onClick={() => setActiveTab("overview")} className="py-3 flex items-center gap-3">
                <Users size={18} className={activeTab === "overview" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "overview" ? "font-bold text-blue-600" : "font-medium"}>Overview</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("procedures")} className="py-3 flex items-center gap-3">
                <FileText size={18} className={activeTab === "procedures" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "procedures" ? "font-bold text-blue-600" : "font-medium"}>Procedures</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("remuneration")} className="py-3 flex items-center gap-3">
                <DollarSign size={18} className={activeTab === "remuneration" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "remuneration" ? "font-bold text-blue-600" : "font-medium"}>Remuneration</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("skills_interests")} className="py-3 flex items-center gap-3">
                <Sparkles size={18} className={activeTab === "skills_interests" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "skills_interests" ? "font-bold text-blue-600" : "font-medium"}>Skills & Interests</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("correspondence")} className="py-3 flex items-center gap-3">
                <Mail size={18} className={activeTab === "correspondence" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "correspondence" ? "font-bold text-blue-600" : "font-medium"}>Correspondence</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("qualifications")} className="py-3 flex items-center gap-3">
                <FileText size={18} className={activeTab === "qualifications" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "qualifications" ? "font-bold text-blue-600" : "font-medium"}>Qualifications</span>
              </DropdownMenuItem>
              {ability.can(ACTIONS.READ, RESOURCES.PERSON_LEARNING) && (
                <DropdownMenuItem onClick={() => setActiveTab("learning")} className="py-3 flex items-center gap-3">
                  <GraduationCap size={18} className={activeTab === "learning" ? "text-blue-500" : "text-gray-400"} />
                  <span className={activeTab === "learning" ? "font-bold text-blue-600" : "font-medium"}>Learning</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setActiveTab("memos_policies")} className="py-3 flex items-center gap-3">
                <FileText size={18} className={activeTab === "memos_policies" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "memos_policies" ? "font-bold text-blue-600" : "font-medium"}>Memos & Policies</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("assets")} className="py-3 flex items-center gap-3">
                <Package size={18} className={activeTab === "assets" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "assets" ? "font-bold text-blue-600" : "font-medium"}>Assets</span>
              </DropdownMenuItem>
              {user.showInAttendance && (
                <DropdownMenuItem onClick={() => setActiveTab("attendance")} className="py-3 flex items-center gap-3">
                  <CalendarCheck size={18} className={activeTab === "attendance" ? "text-blue-500" : "text-gray-400"} />
                  <span className={activeTab === "attendance" ? "font-bold text-blue-600" : "font-medium"}>Attendance & Leave</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setActiveTab("business_systems")} className="py-3 flex items-center gap-3">
                <Monitor size={18} className={activeTab === "business_systems" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "business_systems" ? "font-bold text-blue-600" : "font-medium"}>Business Systems</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("attachments")} className="py-3 flex items-center gap-3">
                <FileText size={18} className={activeTab === "attachments" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "attachments" ? "font-bold text-blue-600" : "font-medium"}>Attachments</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setActiveTab("activity_log")} className="py-3 flex items-center gap-3">
                <History size={18} className={activeTab === "activity_log" ? "text-blue-500" : "text-gray-400"} />
                <span className={activeTab === "activity_log" ? "font-bold text-blue-600" : "font-medium"}>Activity Logs</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Desktop View for Tabs */}
        <TabsList className="hidden md:flex bg-gray-100 p-1 rounded-full w-fit flex-wrap gap-1">
          <TabsTrigger value="overview" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="procedures" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Procedures
          </TabsTrigger>
          <TabsTrigger value="remuneration" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Remuneration
          </TabsTrigger>
          <TabsTrigger value="skills_interests" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Skills & Interests
          </TabsTrigger>
          <TabsTrigger value="correspondence" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Correspondence
          </TabsTrigger>
          <TabsTrigger value="qualifications" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Qualifications
          </TabsTrigger>
          {ability.can(ACTIONS.READ, RESOURCES.PERSON_LEARNING) && (
            <TabsTrigger value="learning" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
              Learning
            </TabsTrigger>
          )}
          <TabsTrigger value="memos_policies" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Memos & Policies
          </TabsTrigger>
          <TabsTrigger value="assets" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Assets
          </TabsTrigger>
          {user.showInAttendance && (
            <TabsTrigger value="attendance" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
              Attendance & Leave
            </TabsTrigger>
          )}
          <TabsTrigger value="business_systems" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Business Systems
          </TabsTrigger>
          <TabsTrigger value="attachments" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Attachments
          </TabsTrigger>
          <TabsTrigger value="activity_log" className="rounded-full px-4 py-2 data-[state=active]:bg-white">
            Activity Logs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Profile Card with Contact & Employment */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                {/* Avatar */}
                <div className="bg-gray-200 rounded-full h-20 w-20 flex-shrink-0 flex items-center justify-center">
                  <span className="text-2xl font-medium text-gray-600">
                    {user.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('')}
                  </span>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-2xl font-semibold">{user.name}</h2>
                  <p className="text-muted-foreground">{user.jobRole}</p>
                  {userManager && user.manager && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Reports to <Link to={`/people/${userManager.id}`} className="hover:underline text-primary font-medium">{userManager.name}</Link>
                    </p>
                  )}
                </div>
              </div>

              <hr className="my-5" />

              {/* Contact & Employment Grid */}
              <div className="grid gap-6 md:grid-cols-3">
                {/* Contact Information */}
                <div className="space-y-4 text-sm">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Contact Information</h3>
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail size={16} className="text-muted-foreground flex-shrink-0" />
                    <span
                      className="truncate cursor-pointer hover:text-primary transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText(user.email);
                        toast.success("Email copied to clipboard");
                      }}
                      title="Click to copy email"
                    >
                      {user.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>{user.mobile || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>{user.residentialAddress || "Not provided"}</span>
                  </div>
                </div>

                {/* Employment & Personal */}
                <div className="space-y-4 text-sm">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Employment & Personal</h3>
                  <div className="flex items-center gap-3">
                    <Shield size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Role: {user.userRole}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Cake size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Date of Birth: {user.dateOfBirth ? toSydneyDate(user.dateOfBirth) : "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Start Date: {user.startDate ? toSydneyDate(user.startDate) : "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Status: {user.employmentStatus || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Employment Entity: {user.employmentEntity && typeof user.employmentEntity === "object" ? user.employmentEntity.name : (user.employmentEntity || "Not set")}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>
                      Subcontractor:{" "}
                      {user.isSubcontractor === true
                        ? `Yes — ${
                            user.subcontractorVendor?.tradingName ||
                            user.subcontractorVendor?.legalName ||
                            "Vendor not set"
                          }`
                        : user.isSubcontractor === false
                          ? "No"
                          : "Not set"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Work Location: {user.workLocation || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <UserCog size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Gender: {user.gender || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <HeartHandshake size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Aboriginal and/or Torres Strait Islander: {user.indigenousStatus || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe2 size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Country of Birth: {user.countryOfBirth || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Accessibility size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Disability Status: {user.disabilityStatus || "Not set"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>Time with company: {user.startDate ? calculateTimeWithCompany(user.startDate) : "Not set"}</span>
                  </div>
                </div>

                {/* Emergency Contact Information */}
                <div className="space-y-4 text-sm">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Emergency Contact Information</h3>
                  <div className="flex items-center gap-3">
                    <UserIcon size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>{user.emergencyContactName || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>{user.emergencyContactNumber || "Not provided"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={16} className="text-muted-foreground flex-shrink-0" />
                    <span>{user.emergencyContactRelationship || "Not provided"}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current responsibilities — what the Archive Person journey has to hand over */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Current responsibilities</CardTitle>
                {archiveChecksLoading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { type: 'assigned_assets', label: 'Assets' },
                  { type: 'business_systems', label: 'Business Systems' },
                  { type: 'owned_procedures', label: 'Owned Procedures' },
                  { type: 'managed_people', label: 'Manager Of' }
                ].map((tile) => (
                  <div key={tile.type} className="rounded-lg border bg-slate-50 px-3.5 py-3">
                    <div className="text-xl font-bold tracking-tight">
                      {archiveChecksLoading ? '—' : archiveBlockers.find((blocker) => blocker.type === tile.type)?.count || 0}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{tile.label}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Profile Access & Departments Side by Side */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Profile Access */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Profile Access</CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {viewersLoading ? "..." : profileViewers.length}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  These people have access to {user.name}&apos;s profile.
                </p>
              </CardHeader>
              <CardContent className="text-sm">
                {viewersLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 size={16} className="animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground text-xs">Loading...</span>
                  </div>
                ) : profileViewers.length > 0 ? (
                  <div className="space-y-2">
                    {(viewersExpanded ? profileViewers : profileViewers.slice(0, 5)).map((viewer) => (
                      <div key={viewer.id} className="flex items-center gap-2 py-1">
                        <div className="bg-gray-100 rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-medium text-gray-500">
                            {viewer.name.split(' ').map((n: string) => n[0]).slice(0, 2).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link to={`/people/${viewer.id}`} className="text-xs font-medium hover:underline text-primary truncate block">
                            {viewer.name}
                          </Link>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-muted-foreground truncate">{viewer.jobRole}</span>
                            <span className="text-[10px] text-muted-foreground">·</span>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{viewer.reason}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {profileViewers.length > 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-muted-foreground h-7"
                        onClick={() => setViewersExpanded(!viewersExpanded)}
                      >
                        {viewersExpanded ? "Show less" : `Show ${profileViewers.length - 5} more`}
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-2">No viewers found</p>
                )}
              </CardContent>
            </Card>

            {/* Departments */}
            <PersonDepartments user={user} loggedInUser={currentUser} readOnly={false} />
          </div>
        </TabsContent>

        <TabsContent value="procedures" className="space-y-6 mt-6">
          {/* Procedures Owned Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Procedures Owned</CardTitle>
              <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_OWNERSHIP }]}>
                {userProcedures.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => openBatchTransferDialog('procedures')}
                    disabled={selectedProcedureIds.length === 0}
                  >
                    <UserCog className="h-3 w-3 mr-1" />
                    Transfer Selected ({selectedProcedureIds.length})
                  </Button>
                )}
              </CanAny>
            </CardHeader>
            <CardContent>
              {/* Batch action bar for procedures */}
              {userProcedures.length > 0 && (
                <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 rounded-md">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={userProcedures.length > 0 && userProcedures.every(p => selectedProcedureIds.includes(p.id))}
                      onCheckedChange={handleProcedureSelectAll}
                    />
                    <span className="text-sm text-blue-900">
                      {selectedProcedureIds.length} selected
                    </span>
                  </div>
                  {selectedProcedureIds.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                      onClick={() => setSelectedProcedureIds([])}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
              {/* Mobile View: List */}
              <div className="md:hidden space-y-4">
                {userProcedures.length > 0 ? userProcedures.map((procedure) => (
                  <div key={procedure.id} className="pb-4 border-b last:border-b-0">
                    <div className="flex items-start gap-2">
                      <div className="pt-1">
                        <Checkbox
                          checked={selectedProcedureIds.includes(procedure.id)}
                          onCheckedChange={() => handleProcedureToggle(procedure.id)}
                        />
                      </div>
                      <div className="flex-1">
                        <Link to={`/procedure/${procedure.id}`} className="font-semibold text-primary hover:underline">{procedure.procedureName}</Link>
                        <div className="flex justify-between text-sm text-muted-foreground mt-1">
                          <span>Cadence</span>
                          <span>{procedure.procedureCadence}</span>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Created</span>
                          <span>{procedure.createdAt ? toSydneyDate(procedure.createdAt) : "N/A"}</span>
                        </div>
                        <div className="mt-2">
                          <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_OWNERSHIP }]}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => openTransferDialog(procedure)}
                            >
                              <UserCog className="h-3 w-3 mr-1" />
                              Transfer Ownership
                            </Button>
                          </CanAny>
                        </div>
                      </div>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-muted-foreground py-4">No procedures owned</p>
                )}
              </div>
              {/* Desktop View: Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={userProcedures.length > 0 && userProcedures.every(p => selectedProcedureIds.includes(p.id))}
                          onCheckedChange={handleProcedureSelectAll}
                        />
                      </TableHead>
                      <TableHead>Procedure</TableHead>
                      <TableHead>Cadence</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-[180px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userProcedures.length > 0 ? userProcedures.map((procedure) => (
                      <TableRow key={procedure.id} className={selectedProcedureIds.includes(procedure.id) ? "bg-blue-50/50" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={selectedProcedureIds.includes(procedure.id)}
                            onCheckedChange={() => handleProcedureToggle(procedure.id)}
                          />
                        </TableCell>
                        <TableCell><Link to={`/procedure/${procedure.id}`} className="hover:underline text-primary font-medium">{procedure.procedureName}</Link></TableCell>
                        <TableCell>{procedure.procedureCadence}</TableCell>
                        <TableCell>{procedure.createdAt ? toSydneyDate(procedure.createdAt) : "N/A"}</TableCell>
                        <TableCell>
                          <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_OWNERSHIP }]}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => openTransferDialog(procedure)}
                            >
                              <UserCog className="h-3 w-3 mr-1" />
                              Transfer
                            </Button>
                          </CanAny>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={5} className="text-center h-24">No procedures owned</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Assigned Activities Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Assigned Activities</CardTitle>
              <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_ACTIVITY }]}>
                {userActivities.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    onClick={() => openBatchTransferDialog('activities')}
                    disabled={selectedActivityIds.length === 0 || !canBatchTransferActivities()}
                  >
                    <UserCog className="h-3 w-3 mr-1" />
                    Transfer Selected ({selectedActivityIds.length})
                  </Button>
                )}
              </CanAny>
            </CardHeader>
            <CardContent>
              {/* Batch action bar for activities */}
              {userActivities.length > 0 && (
                <div className="flex items-center justify-between mb-3 p-2 bg-blue-50 rounded-md">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={userActivities.length > 0 && userActivities.every(a => selectedActivityIds.includes(a.id))}
                      onCheckedChange={handleActivitySelectAll}
                    />
                    <span className="text-sm text-blue-900">
                      {selectedActivityIds.length} selected
                    </span>
                  </div>
                  {selectedActivityIds.length > 0 && (
                    <div className="flex items-center gap-2">
                      {!canBatchTransferActivities() && selectedActivityIds.length > 1 && (
                        <span className="text-xs text-amber-600">Mixed roles</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                        onClick={() => setSelectedActivityIds([])}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* Mobile View: List */}
              <div className="md:hidden space-y-4">
                {userActivities.length > 0 ? userActivities.map((activity) => {
                  const procedure = typeof activity.procedure !== 'string' ? activity.procedure : null;
                  const role = getUserRaciRole(activity);
                  const isSelected = selectedActivityIds.includes(activity.id);
                  return (
                    <div key={activity.id} className={`pb-4 border-b last:border-b-0 ${isSelected ? 'bg-blue-50/50' : ''}`}>
                      <div className="flex items-start gap-2">
                        <div className="pt-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleActivityToggle(activity.id)}
                          />
                        </div>
                        <div className="flex-1">
                          <Link to={`/activities/${activity.id}`} className="font-semibold hover:underline truncate">{activity.activityName}</Link>
                          <div className="flex justify-between text-sm text-muted-foreground mt-1">
                            <span>Role</span>
                            <span>{role}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Procedure</span>
                            {procedure ? (
                              <Link to={`/procedure/${procedure.id}`} className="hover:underline text-primary block break-words">
                                {procedure.procedureName}
                              </Link>
                            ) : "N/A"}
                          </div>
                          <div className="mt-2">
                            <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_ACTIVITY }]}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs"
                                onClick={() => openActivityTransferDialog(activity)}
                              >
                                <UserCog className="h-3 w-3 mr-1" />
                                Transfer Role
                              </Button>
                            </CanAny>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-center text-muted-foreground py-4">No activities assigned</p>
                )}
              </div>
              {/* Desktop View: Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={userActivities.length > 0 && userActivities.every(a => selectedActivityIds.includes(a.id))}
                          onCheckedChange={handleActivitySelectAll}
                        />
                      </TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Procedure</TableHead>
                      <TableHead className="w-[180px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userActivities.length > 0 ? userActivities.map((activity) => {
                      const procedure = typeof activity.procedure !== 'string' ? activity.procedure : null;
                      const role = getUserRaciRole(activity);
                      const isSelected = selectedActivityIds.includes(activity.id);
                      return (
                        <TableRow key={activity.id} className={isSelected ? "bg-blue-50/50" : ""}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleActivityToggle(activity.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Link to={`/activities/${activity.id}`} className="hover:underline font-medium">{activity.activityName}</Link></TableCell>
                          <TableCell>{role}</TableCell>
                          <TableCell>{procedure ? <Link to={`/procedure/${procedure.id}`} className="hover:underline text-primary">{procedure.procedureName}</Link> : "N/A"}</TableCell>
                          <TableCell>
                            <CanAny of={[{ I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE }, { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_ACTIVITY }]}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={() => openActivityTransferDialog(activity)}
                              >
                                <UserCog className="h-3 w-3 mr-1" />
                                Transfer
                              </Button>
                            </CanAny>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow><TableCell colSpan={5} className="text-center h-24">No activities assigned</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="remuneration" className="mt-6">
          {id && user && (
            <RemunerationTab
              user={user}
              currentUser={currentUser}
              onUserUpdate={(updatedUser) => setUser(updatedUser)}
            />
          )}
        </TabsContent>

        <TabsContent value="skills_interests" className="mt-6">
          {id && user && (
            <SkillsAndInterestsTab
              userId={id}
              user={user}
              canEdit={
                currentUser?.userRole === 'Admin' ||
                currentUser?.userRole === 'Director' ||
                (user.manager != null && currentUser?.id === (typeof user.manager === 'string' ? user.manager : user.manager))
              }
              onUserUpdate={(updatedUser) => setUser(updatedUser)}
            />
          )}
        </TabsContent>

        <TabsContent value="correspondence" className="mt-6">
          {id && <CorrespondenceTab userId={id} managerId={user?.manager || null} highlightNoteId={searchParams.get("note")} />}
        </TabsContent>

        <TabsContent value="qualifications" className="mt-6">
          {id && <QualificationsTab userId={id} />}
        </TabsContent>

        {id && ability.can(ACTIONS.READ, RESOURCES.PERSON_LEARNING) && (
          <TabsContent value="learning" className="mt-6">
            <PersonLearningTab userId={id} />
          </TabsContent>
        )}

        <TabsContent value="memos_policies" className="mt-6">
          {id && <MemosAndPoliciesTab userId={id} />}
        </TabsContent>

        <TabsContent value="assets" className="mt-6">
          {id && <AssetsTab userId={id} />}
        </TabsContent>

        {user.showInAttendance && id && (
          <TabsContent value="attendance" className="mt-6">
            <AttendanceTab userId={id} />
          </TabsContent>
        )}

        <TabsContent value="business_systems" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor size={18} className="text-blue-500" />
                Business Systems
              </CardTitle>
            </CardHeader>
            <CardContent>
              {businessSystemsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading business systems...</span>
                </div>
              ) : userBusinessSystems.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No business systems assigned</p>
              ) : (
                <>
                  {/* Mobile View: List */}
                  <div className="md:hidden space-y-4">
                    {userBusinessSystems.map((system) => {
                      const assignment = system.assignedUsers?.find((a: any) => a.user?.id == id);
                      return (
                        <div key={system.id} className="pb-4 border-b last:border-b-0">
                          <div className="font-semibold text-primary">{system.name}</div>
                          <div className="flex justify-between text-sm text-muted-foreground mt-1">
                            <span>Category</span>
                            <span>{system.category || "N/A"}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Status</span>
                            <span className={system.status === 'Active' ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>{system.status}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Role</span>
                            <span>{assignment?.role || "User"}</span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>Monthly Cost</span>
                            <span>${system.monthlyCost ? parseFloat(system.monthlyCost).toLocaleString() : "0"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Desktop View: Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>System</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Monthly Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userBusinessSystems.map((system) => {
                          const assignment = system.assignedUsers?.find((a: any) => a.user?.id == id);
                          return (
                            <TableRow key={system.id}>
                              <TableCell>
                                <div className="font-medium">{system.name}</div>
                                {system.vendor && <div className="text-xs text-muted-foreground">{system.vendor}</div>}
                              </TableCell>
                              <TableCell>{system.category || "N/A"}</TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  system.status === 'Active' ? 'bg-green-100 text-green-800' :
                                  system.status === 'Inactive' ? 'bg-amber-100 text-amber-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {system.status}
                                </span>
                              </TableCell>
                              <TableCell>{assignment?.role || "User"}</TableCell>
                              <TableCell>${system.monthlyCost ? parseFloat(system.monthlyCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments" className="space-y-6 mt-6">
          <FileAttachments
            resourceType="users"
            resourceId={user.id}
            attachments={attachments}
            onAttachmentsChange={handleAttachmentsChange}
            disabled={user.archived}
          />
        </TabsContent>

        <TabsContent value="activity_log" className="space-y-6 mt-6">
          <ActivityLog
            logs={user.activityLogs || []}
            entityName="User"
            users={users}
            currentUser={currentUser}
          />
        </TabsContent>
      </Tabs>

      {/* Unarchive Person Dialog */}
      <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Unarchive Person</DialogTitle>
            <DialogDescription>
              Unarchiving will make this person active again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to unarchive {user.name}?</p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUnarchive} className="gap-2">
              <RefreshCw size={16} />
              Unarchive Person
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Person Dialog */}
      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Delete Person</DialogTitle>
          </DialogHeader>
          {!canDeleteUser() ? (
            <div className="space-y-4">
              <div className="text-sm text-destructive font-medium">
                This person cannot be deleted due to the following:
              </div>
              <div className="space-y-2 text-sm">
                {getManagedPeopleCount() > 0 && (
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-muted-foreground" />
                    <span>Manages {getManagedPeopleCount()} {getManagedPeopleCount() === 1 ? 'person' : 'people'}</span>
                  </div>
                )}
                {getOwnedProceduresCount() > 0 && (
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-muted-foreground" />
                    <span>Currently owns {getOwnedProceduresCount()} {getOwnedProceduresCount() === 1 ? 'procedure' : 'procedures'}</span>
                  </div>
                )}
                {hasHistoricallyOwnedProcedures() && (
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-muted-foreground" />
                    <span>Has previously owned procedures</span>
                  </div>
                )}
                {hasHistoricalActivities() && (
                  <div className="flex items-center gap-2">
                    <History size={16} className="text-muted-foreground" />
                    <span>Has historical data linked to activities</span>
                  </div>
                )}
                {getManagerPositionBlockers().map((blocker, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {blocker.type === 'service_desk_admin' && <Shield size={16} className="text-muted-foreground" />}
                    {blocker.type === 'yard_manager' && <Warehouse size={16} className="text-muted-foreground" />}
                    <span>{blocker.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                You can archive them to keep a historical record while making them inactive in the system.
              </p>
            </div>
          ) : (
            <div className="py-4">
              <p>Are you sure you want to delete "{user.name}"?</p>
              <p className="text-sm text-muted-foreground mt-2">This action cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={archiveChecksLoading || !canDeleteUser()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Transfer Ownership</DialogTitle>
            <DialogDescription className="text-sm">
              Transfer ownership of "{selectedProcedure?.procedureName}" to another user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select New Owner</label>
              <Combobox
                options={Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                  .filter(u => ['Worker', 'Team Lead', 'Supervisor', 'Manager', 'Senior Manager', 'Director', 'Admin', 'Account Owner'].includes(u.userRole))
                  .filter(u => u.id !== id && !u.archived)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(user => ({
                    label: `${user.name} (${user.jobRole || 'none'})`,
                    value: String(user.id),
                  }))}
                value={newOwnerId}
                onChange={(value) => setNewOwnerId(value || "")}
                placeholder="Choose a new owner..."
                searchPlaceholder="Search owner..."
                emptyMessage="No user found"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => { setNewOwnerId(""); setSelectedProcedure(null); }}>Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleTransferOwnership}
              disabled={!newOwnerId || transferLoading}
              className="gap-2"
            >
              {transferLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCog size={16} />
              )}
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity RACI Transfer Dialog */}
      <Dialog open={activityTransferDialogOpen} onOpenChange={setActivityTransferDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Transfer Activity Role</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedActivityRole && (
                <>Update <strong>{selectedActivityRole}</strong> role for "{selectedActivity?.activityName}"</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {(selectedActivityRole === "Responsible" || selectedActivityRole === "Accountable") && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select New {selectedActivityRole}</Label>
                <SearchableSelect
                  groups={raciSingleSelectGroups}
                  value={raciNewValue}
                  onChange={(value) => setRaciNewValue(value)}
                  placeholder={`Select new ${selectedActivityRole?.toLowerCase()}...`}
                  searchPlaceholder="Search users, roles, or partners..."
                  emptyMessage="No results found."
                  noneOption={{ value: 'user-none', label: 'None' }}
                />
              </div>
            )}
            {(selectedActivityRole === "Consulted" || selectedActivityRole === "Informed") && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select {selectedActivityRole} Parties</Label>
                <MultiSelect
                  options={raciMultiSelectOptions}
                  selected={raciMultiValue}
                  onChange={(selectedValues) => setRaciMultiValue(selectedValues)}
                  placeholder={`Select ${selectedActivityRole?.toLowerCase()} parties...`}
                  emptyMessage="No results found."
                />
                <p className="text-xs text-muted-foreground">
                  Deselect yourself to remove this activity from your assigned list.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => { setRaciNewValue(""); setRaciMultiValue([]); setSelectedActivity(null); setSelectedActivityRole(""); }}>Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleActivityRaciTransfer}
              disabled={
                activityTransferLoading ||
                ((selectedActivityRole === "Responsible" || selectedActivityRole === "Accountable") && !raciNewValue)
              }
              className="gap-2"
            >
              {activityTransferLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCog size={16} />
              )}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Transfer Dialog */}
      <Dialog open={batchTransferDialogOpen} onOpenChange={setBatchTransferDialogOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">
              {batchTransferType === 'procedures' 
                ? `Transfer ${selectedProcedureIds.length} Procedure${selectedProcedureIds.length === 1 ? '' : 's'}`
                : `Transfer ${selectedActivityIds.length} Activity${selectedActivityIds.length === 1 ? '' : 'ies'}`
              }
            </DialogTitle>
            <DialogDescription className="text-sm">
              {batchTransferType === 'procedures' 
                ? `Transfer ownership of ${selectedProcedureIds.length} selected procedure${selectedProcedureIds.length === 1 ? '' : 's'} to another user.`
                : `Transfer RACI role for ${selectedActivityIds.length} selected activity${selectedActivityIds.length === 1 ? '' : 'ies'} to another party.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {batchTransferType === 'procedures' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select New Owner</label>
                <Combobox
                  options={Array.from(new Map(users.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                    .filter(u => ['Worker', 'Team Lead', 'Supervisor', 'Manager', 'Senior Manager', 'Director', 'Admin', 'Account Owner'].includes(u.userRole))
                    .filter(u => u.id !== id && !u.archived)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(user => ({
                      label: `${user.name} (${user.jobRole || 'none'})`,
                      value: String(user.id),
                    }))}
                  value={batchNewOwnerId}
                  onChange={(value) => setBatchNewOwnerId(value || "")}
                  placeholder="Choose a new owner..."
                  searchPlaceholder="Search owner..."
                  emptyMessage="No user found"
                />
              </div>
            ) : (
              <>
                {(() => {
                  const selectedActs = userActivities.filter(a => selectedActivityIds.includes(a.id));
                  const roles = selectedActs.map(a => getUserRaciRole(a));
                  const uniqueRoles = [...new Set(roles)];
                  const role = uniqueRoles[0];
                  return (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground">
                        Role type: <strong>{role}</strong>
                      </div>
                      {(role === "Responsible" || role === "Accountable") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Select New {role}</Label>
                          <SearchableSelect
                            groups={raciSingleSelectGroups}
                            value={batchNewOwnerId}
                            onChange={(value) => setBatchNewOwnerId(value)}
                            placeholder={`Select new ${role?.toLowerCase()}...`}
                            searchPlaceholder="Search users, roles, or partners..."
                            emptyMessage="No results found."
                            noneOption={{ value: 'user-none', label: 'None' }}
                          />
                        </div>
                      )}
                      {(role === "Consulted" || role === "Informed") && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Select {role} Parties</Label>
                          <MultiSelect
                            options={raciMultiSelectOptions}
                            selected={batchNewOwnerId ? batchNewOwnerId.split(',') : []}
                            onChange={(selectedValues) => setBatchNewOwnerId(selectedValues.join(','))}
                            placeholder={`Select ${role?.toLowerCase()} parties...`}
                            emptyMessage="No results found."
                          />
                          <p className="text-xs text-muted-foreground">
                            Deselect yourself to remove these activities from your assigned list.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => { setBatchNewOwnerId(""); }}>Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleBatchTransfer}
              disabled={!batchNewOwnerId || batchTransferLoading}
              className="gap-2"
            >
              {batchTransferLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCog size={16} />
              )}
              Transfer {batchTransferType === 'procedures' ? 'Procedures' : 'Activities'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonPage;
